import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Send, Bot, User, Trash2, Plus, Settings, Pencil, ChevronLeft, Check, Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Simple markdown renderer for bold, italic, headers, lists, horizontal rules
const MarkdownText = ({ text, className = '' }) => {
  const renderMarkdown = (input) => {
    const lines = input.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">
            {listItems.map((li, i) => <li key={i}>{renderInline(li)}</li>)}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    const renderInline = (line) => {
      // Process inline markdown: **bold**, *italic*, `code`
      const parts = [];
      let remaining = line;
      let key = 0;

      while (remaining.length > 0) {
        // Bold: **text**
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        // Italic: *text*
        const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);
        // Code: `text`
        const codeMatch = remaining.match(/`(.+?)`/);

        // Find earliest match
        const matches = [
          boldMatch && { type: 'bold', match: boldMatch },
          italicMatch && { type: 'italic', match: italicMatch },
          codeMatch && { type: 'code', match: codeMatch },
        ].filter(Boolean).sort((a, b) => a.match.index - b.match.index);

        if (matches.length === 0) {
          parts.push(<span key={key++}>{remaining}</span>);
          break;
        }

        const first = matches[0];
        const idx = first.match.index;

        if (idx > 0) {
          parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
        }

        if (first.type === 'bold') {
          parts.push(<strong key={key++} className="font-bold">{first.match[1]}</strong>);
        } else if (first.type === 'italic') {
          parts.push(<em key={key++}>{first.match[1]}</em>);
        } else if (first.type === 'code') {
          parts.push(<code key={key++} className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-xs font-mono">{first.match[1]}</code>);
        }

        remaining = remaining.slice(idx + first.match[0].length);
      }

      return parts;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Headers
      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h4 key={i} className="font-bold text-sm mt-3 mb-1">{renderInline(line.slice(4))}</h4>);
        continue;
      }
      if (line.startsWith('## ')) {
        flushList();
        elements.push(<h3 key={i} className="font-bold mt-3 mb-1">{renderInline(line.slice(3))}</h3>);
        continue;
      }

      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        flushList();
        elements.push(<hr key={i} className="my-2 border-slate-200" />);
        continue;
      }

      // Unordered list items
      if (/^\s*[-*]\s+/.test(line)) {
        inList = true;
        listItems.push(line.replace(/^\s*[-*]\s+/, ''));
        continue;
      }

      // Ordered list items
      if (/^\s*\d+\.\s+/.test(line)) {
        flushList();
        const content = line.replace(/^\s*\d+\.\s+/, '');
        elements.push(
          <div key={i} className="flex gap-2 my-0.5">
            <span className="text-slate-400 flex-shrink-0">{line.match(/^\s*(\d+)\./)[1]}.</span>
            <span>{renderInline(content)}</span>
          </div>
        );
        continue;
      }

      flushList();

      // Empty line
      if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
        continue;
      }

      // Normal paragraph
      elements.push(<p key={i} className="my-0.5">{renderInline(line)}</p>);
    }

    flushList();
    return elements;
  };

  return <div className={`text-sm leading-relaxed ${className}`}>{renderMarkdown(text)}</div>;
};

// ---- Prompt Management Sheet ----
const PromptManagementSheet = ({ open, onOpenChange }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) fetchPrompts();
  }, [open]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ai-assistant/prompts`);
      setPrompts(res.data);
    } catch (e) {
      toast.error('Error al cargar prompts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPrompt(null);
    setFormName('');
    setFormContent('');
    setEditDialogOpen(true);
  };

  const openEditDialog = (prompt) => {
    setEditingPrompt(prompt);
    setFormName(prompt.name);
    setFormContent(prompt.system_prompt);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formContent.trim()) {
      toast.error('Nombre y contenido son obligatorios');
      return;
    }
    setSaving(true);
    try {
      if (editingPrompt) {
        await axios.put(`${API_URL}/ai-assistant/prompts/${editingPrompt.id}`, {
          name: formName,
          system_prompt: formContent
        });
        toast.success('Prompt actualizado');
      } else {
        await axios.post(`${API_URL}/ai-assistant/prompts`, {
          name: formName,
          system_prompt: formContent,
          is_active: prompts.length === 0
        });
        toast.success('Prompt creado');
      }
      setEditDialogOpen(false);
      fetchPrompts();
    } catch (e) {
      toast.error('Error al guardar prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (prompt) => {
    try {
      await axios.put(`${API_URL}/ai-assistant/prompts/${prompt.id}`, {
        is_active: !prompt.is_active
      });
      toast.success(prompt.is_active ? 'Prompt desactivado' : 'Prompt activado');
      fetchPrompts();
    } catch (e) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('¿Eliminar este prompt?')) return;
    try {
      await axios.delete(`${API_URL}/ai-assistant/prompts/${promptId}`);
      toast.success('Prompt eliminado');
      fetchPrompts();
    } catch (e) {
      toast.error('Error al eliminar prompt');
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Configurar Prompts del Asistente</SheetTitle>
            <SheetDescription>
              Define el comportamiento y personalidad del asistente IA. El prompt activo se usará en las conversaciones.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <Button
              data-testid="create-prompt-btn"
              onClick={openCreateDialog}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Nuevo Prompt
            </Button>

            {loading ? (
              <div className="text-center py-8 text-slate-500">Cargando...</div>
            ) : prompts.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-sm text-slate-500">
                  No hay prompts personalizados. Se usa el prompt por defecto del sistema.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {prompts.map((prompt) => (
                  <Card
                    key={prompt.id}
                    data-testid={`prompt-card-${prompt.id}`}
                    className={`p-4 transition-all ${
                      prompt.is_active
                        ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-200'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-sm truncate">{prompt.name}</h4>
                          {prompt.is_active && (
                            <Badge data-testid="active-badge" variant="default" className="bg-blue-600 text-xs">
                              Activo
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2">
                          {prompt.system_prompt.substring(0, 120)}...
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(prompt.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Switch
                          data-testid={`toggle-prompt-${prompt.id}`}
                          checked={prompt.is_active}
                          onCheckedChange={() => handleToggleActive(prompt)}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button
                        data-testid={`edit-prompt-${prompt.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(prompt)}
                        className="flex-1"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        data-testid={`delete-prompt-${prompt.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(prompt.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <Card className="p-4 bg-slate-50 border-dashed">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-sm">Prompt por Defecto</h4>
                {prompts.every(p => !p.is_active) && (
                  <Badge variant="secondary" className="text-xs">
                    <Check className="w-3 h-3 mr-1" />
                    En uso
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Si no hay un prompt personalizado activo, se usa el prompt por defecto del sistema que incluye instrucciones de transporte, Google Maps, y estilo conversacional.
              </p>
            </Card>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPrompt ? 'Editar Prompt' : 'Crear Nuevo Prompt'}
            </DialogTitle>
            <DialogDescription>
              Define las instrucciones que guiarán al asistente IA.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre del Prompt</label>
              <Input
                data-testid="prompt-name-input"
                placeholder="Ej: Asistente Madrid, Modo Formal..."
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">System Prompt</label>
              <Textarea
                data-testid="prompt-content-input"
                placeholder="Escribe las instrucciones para el asistente IA..."
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                rows={14}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-400 mt-1">
                Tip: Puedes incluir instrucciones como el tono, idioma, formato de respuesta, y capacidades especiales.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="save-prompt-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Guardando...' : (editingPrompt ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---- Main AI Assistant Page ----
const AIAssistantPage = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [promptSheetOpen, setPromptSheetOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('none');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    fetchProjects();
  }, []);

  useEffect(() => {
    if (currentSessionId) {
      fetchSessionMessages(currentSessionId);
    }
  }, [currentSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/ai-assistant/sessions`);
      setSessions(response.data);
      if (response.data.length > 0 && !currentSessionId) {
        const firstSession = response.data[0];
        setCurrentSessionId(firstSession.id);
        if (firstSession.project_id) {
          setSelectedProjectId(firstSession.project_id);
        }
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchSessionMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/ai-assistant/sessions/${sessionId}`);
      setMessages(response.data.messages || []);
      if (response.data.project_id) {
        setSelectedProjectId(response.data.project_id);
      } else {
        setSelectedProjectId('none');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    setLoading(true);
    const userMessage = inputMessage;
    setInputMessage('');

    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const payload = {
        message: userMessage,
        session_id: currentSessionId
      };
      if (selectedProjectId && selectedProjectId !== 'none') {
        payload.project_id = selectedProjectId;
      }

      const response = await axios.post(`${API_URL}/ai-assistant/chat`, payload);

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        route_info: response.data.route_info
      }]);

      if (!currentSessionId) {
        setCurrentSessionId(response.data.session_id);
        fetchSessions();
      }
    } catch (error) {
      toast.error('Error al enviar mensaje');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setInputMessage('');
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('¿Eliminar esta conversación?')) return;

    try {
      await axios.delete(`${API_URL}/ai-assistant/sessions/${sessionId}`);
      toast.success('Conversación eliminada');

      if (sessionId === currentSessionId) {
        handleNewChat();
      }

      fetchSessions();
    } catch (error) {
      toast.error('Error al eliminar conversación');
    }
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <div className="w-72 bg-white border-r flex flex-col">
        <div className="p-4 border-b space-y-3">
          <Button
            data-testid="new-chat-button"
            onClick={handleNewChat}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Conversación
          </Button>

          {/* Project selector */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1">
              <Database className="w-3 h-3" />
              Datos GTFS
            </label>
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
            >
              <SelectTrigger data-testid="project-selector" className="w-full text-sm">
                <SelectValue placeholder="Sin datos GTFS" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin datos GTFS</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProject && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {selectedProject.file_stats && Object.entries(selectedProject.file_stats).map(([k, v]) => (
                  <span key={k} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                    {k}: {v}
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button
            data-testid="open-prompts-btn"
            variant="outline"
            onClick={() => setPromptSheetOpen(true)}
            className="w-full"
            size="sm"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurar Prompts
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Conversaciones</h3>
          {sessions.map((session) => (
            <Card
              key={session.id}
              className={`p-3 cursor-pointer transition-colors ${
                session.id === currentSessionId ? 'bg-blue-50 border-blue-500' : 'hover:bg-slate-50'
              }`}
              onClick={() => setCurrentSessionId(session.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    {session.messages?.[0]?.content?.substring(0, 30) || 'Nueva conversación'}...
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {session.project_id && (
                      <Database className="w-3 h-3 text-blue-500" />
                    )}
                    <p className="text-xs text-slate-500">
                      {new Date(session.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSession(session.id);
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <div className="p-4 border-t">
          <Button
            data-testid="back-to-dashboard"
            variant="ghost"
            className="w-full justify-start text-slate-600"
            onClick={() => window.location.href = '/'}
            size="sm"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight" data-testid="ai-assistant-title">
                  Asistente de Rutas IA
                </h1>
                <p className="text-xs text-slate-500">
                  {selectedProject
                    ? <span className="flex items-center gap-1"><Database className="w-3 h-3 text-blue-500 inline" /> Usando datos de: <strong>{selectedProject.name}</strong></span>
                    : 'Sin datos GTFS seleccionados'}
                </p>
              </div>
            </div>
            <Button
              data-testid="header-settings-btn"
              variant="outline"
              size="sm"
              onClick={() => setPromptSheetOpen(true)}
            >
              <Settings className="w-4 h-4 mr-1" />
              Prompts
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Card className="p-8 max-w-lg text-center">
                <Bot className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                <h3 className="text-lg font-semibold mb-2">¡Hola! Soy tu asistente de rutas</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Pregúntame sobre rutas de transporte público. Puedo usar datos GTFS para responder consultas geoespaciales.
                </p>
                {selectedProject ? (
                  <div className="bg-blue-50 rounded-lg p-4 text-left">
                    <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-1">
                      <Database className="w-4 h-4" /> Datos GTFS cargados: {selectedProject.name}
                    </p>
                    <p className="text-xs text-blue-600">Puedes preguntarme sobre:</p>
                    <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs text-blue-700">
                      <li>¿Qué paradas tiene la ruta X?</li>
                      <li>¿Qué rutas pasan cerca de un punto?</li>
                      <li>¿Cuántos km recorre la ruta Y?</li>
                      <li>¿Qué paradas están más cerca de...?</li>
                      <li>Resumen general del feed GTFS</li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-amber-50 rounded-lg p-4 text-left">
                    <p className="text-xs text-amber-700">
                      Selecciona un proyecto GTFS en el panel lateral para habilitar consultas geoespaciales basadas en datos reales.
                    </p>
                  </div>
                )}
              </Card>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}

                <div
                  className={`max-w-2xl rounded-lg p-4 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border shadow-sm'
                  }`}
                >
                  <MarkdownText
                    text={msg.content}
                    className={msg.role === 'user' ? 'text-white' : ''}
                  />

                  {msg.route_info && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-semibold">Distancia:</span>
                          <span>{msg.route_info.distance}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-semibold">Duración:</span>
                          <span>{msg.route_info.duration}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs mt-2 opacity-60">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>

                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="bg-white border-t p-4">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              data-testid="message-input"
              type="text"
              placeholder={selectedProject ? `Pregunta sobre ${selectedProject.name}...` : "Escribe tu pregunta sobre rutas..."}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={loading}
              className="flex-1"
            />
            <Button
              data-testid="send-button"
              type="submit"
              disabled={loading || !inputMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                'Enviando...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Prompt Management Sheet */}
      <PromptManagementSheet
        open={promptSheetOpen}
        onOpenChange={setPromptSheetOpen}
      />
    </div>
  );
};

export default AIAssistantPage;
